/**
 * Verify Composable
 * 驗證流程管理（OTP、銀行綁定、停權檢查）
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._verifyContext 避免重複創建
 */

import { useMainStore } from '@/stores/main'
import { useAuthOtp } from '@/composables/auth/useAuthOtp'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useSite } from '@/composables/core/useSite'

// ========== CheckPoint 常數 ==========

const validationCheckPoint = {
  REGISTER: 'register',
  GET_REWARD: 'getReward',
  DEPOSIT: 'deposit',
  WITHDRAW: 'withdraw',
  ENTER_GAME: 'enterGame',
  BIND_BANK: 'bindBank',
  PROMO_CODE: 'promoCode',
  PROMOTION: 'promotion',
}

const suspensionCheckPoint = {
  REGISTER: 'register',
  GET_REWARD: 'getReward',
  DEPOSIT: 'deposit',
  WITHDRAW: 'withdraw',
  ENTER_GAME: 'enterGame',
  PROMO_CODE: 'promoCode',
  PROMOTION: 'promotion',
}

const bindBankCheckPoint = {
  REGISTER: 'register',
  GET_REWARD: 'getReward',
  DEPOSIT: 'deposit',
  WITHDRAW: 'withdraw',
  ENTER_GAME: 'enterGame',
  PROMO_CODE: 'promoCode',
}

// ========== Composable ==========

export const useVerify = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保只創建一次
  if (!nuxtApp._verifyContext) {
    const mainStore = useMainStore()
    const { setModal, setBindBankStrategy, setBindBankCheckpoint, setBindBankModal, setBindBankResolver } = mainStore
    const { userInfo } = useUserInfo()
    const { otpValidateEndpoint, suspensionValidateEndpoint, bankValidateEndpoint } = useSite()
    const otp = useAuthOtp()

    // ========== 檢查函式 ==========

    const checkValidation = (checkPoint) => {
      const { verifyMethod = [] } = otpValidateEndpoint.value?.[checkPoint] || {}
      return verifyMethod?.[0]
    }

    const checkSuspension = (checkPoint) => {
      return suspensionValidateEndpoint.value?.[checkPoint]
    }

    const checkBindBank = (checkPoint) => {
      return bankValidateEndpoint.value?.[checkPoint]
    }

    // ========== 狀態檢查方法 ==========

    const isSuspended = (type) => {
      const checkPoint = suspensionCheckPoint[type]
      return userInfo.value?.status === 4 && checkSuspension(checkPoint)
    }

    const isVerified = (type) => {
      if (userInfo.value?.verification_status === 1) {
        return true
      }

      const checkPoint = validationCheckPoint[type]
      const method = checkValidation(checkPoint)

      if (method) {
        return false
      }

      const needBindBank = checkBindBank(bindBankCheckPoint[type])
      if (needBindBank && userInfo.value?.bank_setting_status !== 1) {
        const bindBankMethod = checkValidation('bindBank')
        if (bindBankMethod) {
          return false
        }
      }

      return true
    }

    const isBankBound = (type) => {
      const checkPoint = bindBankCheckPoint[type]
      const needBindBank = checkBindBank(checkPoint)
      return userInfo.value?.bank_setting_status === 1 || !needBindBank
    }

    // ========== 流程觸發方法 ==========

    const triggerOtpFlow = async (type, isMandatory = false) => {
      if (isVerified(type)) {
        return true
      }

      const checkPoint = validationCheckPoint[type]
      let method = checkValidation(checkPoint)

      if (!method) {
        const needBindBank = checkBindBank(bindBankCheckPoint[type])
        if (needBindBank && userInfo.value?.bank_setting_status !== 1) {
          method = checkValidation('bindBank')
        }
      }

      return await otp.triggerOtpVerification({ method, checkPoint }, isMandatory)
    }

    const triggerBindBankFlow = (type, isMandatory = false) => {
      if (isBankBound(type)) {
        return Promise.resolve(true)
      }

      return new Promise((resolve) => {
        const checkPoint = bindBankCheckPoint[type]

        // 設定 resolver，等待彈窗完成時調用
        setBindBankResolver(resolve)

        if (isMandatory) {
          const strategy = bankValidateEndpoint.value?.[checkPoint]
          setBindBankStrategy(strategy)
          setBindBankCheckpoint(checkPoint)
          setBindBankModal('ModalBindBankMandatory')
        } else {
          setModal('ModalBindBank')
        }
      })
    }

    // ========== 主要驗證方法 ==========

    const verify = async (type, isMandatory = false) => {
      // 1. 檢查是否被停權
      if (isSuspended(type)) {
        setModal('ModalSuspensionWarning')
        return false
      }

      // 2. OTP 驗證（如果需要）
      if (!isVerified(type)) {
        const otpVerified = await triggerOtpFlow(type, isMandatory)
        if (!otpVerified) {
          return false
        }
      }

      // 3. 銀行綁定（如果需要）- OTP 完成後自動接續
      if (!isBankBound(type)) {
        return triggerBindBankFlow(type, isMandatory)
      }

      return true
    }

    // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
    const cleanup = () => {}

    nuxtApp._verifyContext = {
      // 主要驗證方法
      verify,
      triggerOtpFlow,
      triggerBindBankFlow,

      // 狀態檢查方法（可單獨使用）
      isSuspended,
      isVerified,
      isBankBound,

      // 檢查函式（只需傳入 checkPoint）
      checkValidation,
      checkSuspension,
      checkBindBank,

      // CheckPoint 常數
      validationCheckPoint,
      suspensionCheckPoint,
      bindBankCheckPoint,
      cleanup,
    }
  }

  return nuxtApp._verifyContext
}
